import { useEffect, useState } from 'react';
import type { Order } from '../types/trade';
import { api } from '../services/api';

interface OrderManagementProps {
  refreshInterval?: number;
}

export function OrderManagement({ refreshInterval = 5000 }: OrderManagementProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');

  // New order form state
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [newOrder, setNewOrder] = useState({
    account: 'ROKMM',
    side: 'buy' as 'buy' | 'sell',
    type: 'limit' as 'limit' | 'market',
    price: '',
    quantity: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadOrders = async () => {
    try {
      const data = await api.getOpenOrders();
      setOrders(data);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId: string, account: string, symbol: string = 'fco_usdt') => {
    try {
      setCancelling(orderId);
      await api.cancelOrder(orderId, account, symbol);
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel order');
    } finally {
      setCancelling(null);
    }
  };

  const submitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrder.quantity) return;

    try {
      setSubmitting(true);
      await api.placeOrder({
        account: newOrder.account,
        symbol: 'fco_usdt',
        side: newOrder.side,
        type: newOrder.type,
        price: newOrder.type === 'limit' ? parseFloat(newOrder.price) : undefined,
        quantity: parseFloat(newOrder.quantity),
      });
      setShowNewOrder(false);
      setNewOrder({ account: 'ROKMM', side: 'buy', type: 'limit', price: '', quantity: '' });
      await loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === 'all') return true;
    if (filter === 'buy') return order.side === 'buy';
    if (filter === 'sell') return order.side === 'sell';
    return order.account === filter;
  });

  const formatPrice = (price: number) => `$${price.toFixed(5)}`;
  const formatQty = (qty: number) => qty.toLocaleString(undefined, { maximumFractionDigits: 0 });

  if (loading && orders.length === 0) {
    return <div className="order-management loading">Loading orders...</div>;
  }

  return (
    <div className="order-management">
      <div className="orders-header">
        <h3>Order Management</h3>
        <div className="header-controls">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Orders</option>
            <option value="buy">Buy Only</option>
            <option value="sell">Sell Only</option>
            <option value="ROKMM">ROKMM</option>
            <option value="ROKVOL">ROKVOL</option>
            <option value="TREASURY">TREASURY</option>
          </select>
          <button onClick={() => setShowNewOrder(!showNewOrder)} className="new-order-btn">
            {showNewOrder ? 'Cancel' : '+ New Order'}
          </button>
          <button onClick={loadOrders} className="refresh-btn" title="Refresh">
            ↻
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showNewOrder && (
        <form className="new-order-form" onSubmit={submitOrder}>
          <div className="form-row">
            <label>Account</label>
            <select
              value={newOrder.account}
              onChange={(e) => setNewOrder({ ...newOrder, account: e.target.value })}
            >
              <option value="ROKMM">ROKMM</option>
              <option value="ROKVOL">ROKVOL</option>
              <option value="TREASURY">TREASURY</option>
            </select>
          </div>
          <div className="form-row">
            <label>Side</label>
            <div className="side-buttons">
              <button
                type="button"
                className={`side-btn buy ${newOrder.side === 'buy' ? 'active' : ''}`}
                onClick={() => setNewOrder({ ...newOrder, side: 'buy' })}
              >
                Buy
              </button>
              <button
                type="button"
                className={`side-btn sell ${newOrder.side === 'sell' ? 'active' : ''}`}
                onClick={() => setNewOrder({ ...newOrder, side: 'sell' })}
              >
                Sell
              </button>
            </div>
          </div>
          <div className="form-row">
            <label>Type</label>
            <select
              value={newOrder.type}
              onChange={(e) => setNewOrder({ ...newOrder, type: e.target.value as 'limit' | 'market' })}
            >
              <option value="limit">Limit</option>
              <option value="market">Market</option>
            </select>
          </div>
          {newOrder.type === 'limit' && (
            <div className="form-row">
              <label>Price</label>
              <input
                type="number"
                step="0.00001"
                placeholder="0.00000"
                value={newOrder.price}
                onChange={(e) => setNewOrder({ ...newOrder, price: e.target.value })}
                required
              />
            </div>
          )}
          <div className="form-row">
            <label>Quantity (FCO)</label>
            <input
              type="number"
              step="1"
              placeholder="0"
              value={newOrder.quantity}
              onChange={(e) => setNewOrder({ ...newOrder, quantity: e.target.value })}
              required
            />
          </div>
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Placing...' : 'Place Order'}
          </button>
        </form>
      )}

      <div className="orders-table">
        <div className="table-header">
          <span>Time</span>
          <span>Account</span>
          <span>Side</span>
          <span>Price</span>
          <span>Qty</span>
          <span>Filled</span>
          <span>Status</span>
          <span>Action</span>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="no-orders">No open orders</div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.orderId} className={`order-row ${order.side}`}>
              <span className="time">
                {new Date(order.createdAt).toLocaleTimeString()}
              </span>
              <span className="account">{order.account}</span>
              <span className={`side ${order.side}`}>{order.side.toUpperCase()}</span>
              <span className="price">{formatPrice(order.price)}</span>
              <span className="quantity">{formatQty(order.quantity)}</span>
              <span className="filled">
                {formatQty(order.filled)} ({((order.filled / order.quantity) * 100).toFixed(0)}%)
              </span>
              <span className={`status ${order.status}`}>{order.status}</span>
              <span className="actions">
                <button
                  className="cancel-btn"
                  onClick={() => cancelOrder(order.orderId, order.account, order.symbol)}
                  disabled={cancelling === order.orderId}
                >
                  {cancelling === order.orderId ? '...' : 'Cancel'}
                </button>
              </span>
            </div>
          ))
        )}
      </div>

      <div className="orders-summary">
        <span>Total: {filteredOrders.length} orders</span>
        <span>Buy: {filteredOrders.filter((o) => o.side === 'buy').length}</span>
        <span>Sell: {filteredOrders.filter((o) => o.side === 'sell').length}</span>
      </div>
    </div>
  );
}
